import React, { useContext, useEffect } from 'react'
import { AppSettings } from '../../config/app-settings'
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader
} from '../../components/other/card/card'
import { useFormik } from 'formik'
import { getUserId } from '../../helpers/auth'
import * as Yup from 'yup'
import { Input, Select } from 'antd'
import TextArea from 'antd/es/input/TextArea'
import { useUpdateDomainBulkMutation } from '../../redux/apis/domainsManagement/domains/dMDomainsApiSlice'
import { toast } from 'react-toastify'
import Loader from '../pages/loader'

const BulkUpdateReprtingMethod = React.memo(() => {
  const [update, { isLoading, error, isSuccess }] =
    useUpdateDomainBulkMutation()
  const context = useContext(AppSettings)
  function cleanUrls (urls) {
    return urls.map(url => {
      // Remove "www.", "https://", "http://", and anything after common domain suffixes
      return url.replace(
        /^(https?:\/\/)?(www\.)?([^\/]+)(\.[a-z]{2,})(\/.*)?$/,
        '$3$4'
      )
    })
  }

  const validation = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    initialValues: {
      domains: '',
      reporting_abuse_to: null,
      domain_abuse_email: null,
      domain_abuse_form: null,
      domain_proxy_abuse_email: null,
      domain_proxy_abuse_form: null,
      last_updated_by_id: getUserId()
    },

    validationSchema: Yup.object({
      domains: Yup.string().required('Please Enter Domains'),

      reporting_abuse_to: Yup.string().required(
        'Please Enter Reporting Abuse To'
      ),
      domain_abuse_email: Yup.string().when(
        'reporting_abuse_to',
        ([reporting_abuse_to], schema) =>
          reporting_abuse_to === 'website_email'
            ? schema.trim().required('Please enter domain abuse email')
            : schema.nullable()
      ),
      domain_abuse_form: Yup.string().when(
        'reporting_abuse_to',
        ([reporting_abuse_to], schema) =>
          reporting_abuse_to === 'website_form'
            ? schema.trim().required('Please enter domain abuse form')
            : schema.nullable()
      ),
      domain_proxy_abuse_email: Yup.string().when(
        'reporting_abuse_to',
        ([reporting_abuse_to], schema) =>
          reporting_abuse_to === 'proxy_email'
            ? schema.trim().required('Please enter isp abuse email')
            : schema.nullable()
      ),
      domain_proxy_abuse_form: Yup.string().when(
        'reporting_abuse_to',
        ([reporting_abuse_to], schema) =>
          reporting_abuse_to === 'proxy_form'
            ? schema.trim().required('Please enter isp abuse form')
            : schema.nullable()
      )
    }),

    onSubmit: async values => {
      const domains = values.domains
        .split('\n')
        .map(url => url.trim())
        .filter(url => url)
      const cleanDomains = cleanUrls(domains)

      const data = cleanDomains.map(domain => {
        return {
          domain_name: domain,
          reporting_abuse_to: values.reporting_abuse_to,
          domain_abuse_email: values.domain_abuse_email,
          domain_abuse_form: values.domain_abuse_form,
          domain_proxy_abuse_email: values.domain_proxy_abuse_email,
          domain_proxy_abuse_form: values.domain_proxy_abuse_form,
          last_updated_by_id: getUserId()
        }
      })

      try {
        const res = await update({ body: data }).unwrap()
      } catch (error) {
        console.error(error)
      }
    }
  })
  useEffect(() => {
    if (error?.data) {
      const errorMessages = Object.values(error.data).flat()
      errorMessages.forEach(message => {
        toast.error(message)
      })
    }
  }, [error])

  useEffect(() => {
    if (isSuccess) {
      toast.success('Domains Updated successfully!')
    }
  }, [isSuccess])

  useEffect(() => {
    context.setAppHeaderNone(false)
    context.setAppSidebarNone(false)
    context.setAppContentClass('')
    context.setAppThemePanal(true)

    return () => {
      context.setAppHeaderNone(true)
      context.setAppSidebarNone(true)
      context.setAppContentClass('p-0')
      context.setAppThemePanal(false)
    }
  }, [context]) // Add context as a dependency

  return (
    <div>
      <Loader isLoading={isLoading} />
      <Card>
        <CardHeader>Domain Reporting Method Bulk Update</CardHeader>
        <CardBody>
          <div className=' mb-3 flex-1'>
            <label>Domain Reporting Abuse To:</label>
            <br />
            <Select
              className={'customSelect'}
              name='reporting_abuse_to'
              style={{
                width: '100%'
              }}
              defaultValue={validation?.values?.reporting_abuse_to}
              value={validation?.values?.reporting_abuse_to}
              onChange={e => validation.setFieldValue('reporting_abuse_to', e)}
              onBlur={validation.handleBlur}
              options={[
                {
                  label: 'ISP Email',
                  value: 'isp_email'
                },
                {
                  label: 'ISP Form',
                  value: 'isp_form'
                },
                {
                  label: 'Report infringement to same website abuse email',
                  value: 'website_email'
                },
                {
                  label: 'Report infringement to same website abuse form',
                  value: 'website_form'
                },
                {
                  label:
                    'Report infringement to real ISP abuse email that is provided by proxy service',
                  value: 'proxy_email'
                },
                {
                  label:
                    'Report infringement to real ISP abuse form that is provided by proxy service',
                  value: 'proxy_form'
                }
              ]}
            />
            {validation.touched.reporting_abuse_to &&
            validation.errors.reporting_abuse_to ? (
              <p className='text-danger'>
                {validation.errors.reporting_abuse_to}
              </p>
            ) : null}
          </div>
          {validation.values.reporting_abuse_to === 'website_email' && (
            <div className=' mb-3 flex-1 customInput'>
              <label className="form-label">
                Report infringement to same website abuse email
              </label>
              <Input
                type='text'
                name='domain_abuse_email'
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.domain_abuse_email}
              />
              {validation.touched.domain_abuse_email &&
              validation.errors.domain_abuse_email ? (
                <p className='text-danger'>
                  {validation.errors.domain_abuse_email}
                </p>
              ) : null}
            </div>
          )}
          {validation.values.reporting_abuse_to === 'website_form' && (
            <div className=' mb-3 flex-1 customInput'>
              <label className="form-label">
                Report infringement to same website abuse form
              </label>
              <Input
                type='text'
                name='domain_abuse_form'
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.domain_abuse_form}
              />
              {validation.touched.domain_abuse_form &&
              validation.errors.domain_abuse_form ? (
                <p className='text-danger'>
                  {validation.errors.domain_abuse_form}
                </p>
              ) : null}
            </div>
          )}
          {validation.values.reporting_abuse_to === 'proxy_email' && (
            <div className=' mb-3 flex-1 customInput'>
              <label className="form-label">
                Report infringement to real ISP abuse email that is provided by
                proxy service
              </label>
              <Input
                type='text'
                name='domain_proxy_abuse_email'
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.domain_proxy_abuse_email}
              />
              {validation.touched.domain_proxy_abuse_email &&
              validation.errors.domain_proxy_abuse_email ? (
                <p className='text-danger'>
                  {validation.errors.domain_proxy_abuse_email}
                </p>
              ) : null}
            </div>
          )}
          {validation.values.reporting_abuse_to === 'proxy_form' && (
            <div className=' mb-3 flex-1 customInput'>
              <label className="form-label">
                Report infringement to real ISP abuse form that is provided by
                proxy service
              </label>
              <Input
                type='text'
                name='domain_proxy_abuse_form'
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.domain_proxy_abuse_form}
              />
              {validation.touched.domain_proxy_abuse_form &&
              validation.errors.domain_proxy_abuse_form ? (
                <p className='text-danger'>
                  {validation.errors.domain_proxy_abuse_form}
                </p>
              ) : null}
            </div>
          )}
          <div className=' mb-3 flex-1 customInput'>
            <label className="form-label">Domains</label>
            <TextArea
              type='text'
              name='domains'
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.domains}
              placeholder='Enter Domain Names separated by new lines
                example: 
                test.com
                test2.com
                test3.com
                '
              rows={15}
            />
            {validation.touched.domains && validation.errors.domains ? (
              <p className='text-danger'>{validation.errors.domains}</p>
            ) : null}
          </div>
        </CardBody>
        <CardFooter>
          <div className='flex justify-end gap-8'>
            <button
              onClick={() => {
                validation.handleSubmit()
              }}
              className='btn btn-theme'
            >
              Submit
            </button>
          </div>
        </CardFooter>
      </Card>
    </div>
  )
})

export default BulkUpdateReprtingMethod
